import { ICreateOptionDto } from '@/api/services/options.service';
import { OptionType } from '@/enums/OptionType';
import { IItem } from '@/interfaces/IItem';
import { IOption } from '@/interfaces/IOption';
import { addonRuleKey, IAddonRuleText } from '@/domain/menu-import/menu-draft';
import type { TGroupTemplate } from './group-templates';
import type { ILinkOverrides, IOptionLinkRow } from './product-form';

/**
 * The «Opciones» section's data (plan §5.13), pure: a group's rule in plain
 * words (with legacy per-link overrides merged), how many products use it,
 * and a group copied as a draft (a new group created on save).
 */

type TRuleSource = Pick<
  ICreateOptionDto,
  'type' | 'multiple' | 'mandatory' | 'min' | 'max'
>;

/** The rule a product applies: the group's, with the link's overrides on top. */
export function effectiveRule(
  group: TRuleSource,
  overrides: ILinkOverrides | null
): TRuleSource {
  return {
    type: group.type,
    multiple: overrides?.multiple ?? group.multiple,
    mandatory: overrides?.mandatory ?? group.mandatory,
    min: overrides?.min ?? group.min,
    max: overrides?.max ?? group.max,
  };
}

/** «Extras · hasta 5», «elija 1»… (the importer's wording). */
export function groupRuleText(rule: TRuleSource): IAddonRuleText {
  return addonRuleKey({
    kind: rule.type === OptionType.Add ? 'add' : 'choose',
    required: rule.mandatory,
    multiple: rule.multiple,
    min: rule.min,
    max: rule.max,
  });
}

/** Group id → ids of the products that link it (live, non-size links). */
export function optionUsage(items: readonly IItem[]): Record<number, number[]> {
  const usage: Record<number, number[]> = {};
  for (const item of items)
    for (const link of item.itemOptions ?? []) {
      const id = link.option?.id ?? link.optionId;
      if (id === undefined || link.option?.main) continue;
      (usage[id] = usage[id] ?? []).push(item.id);
    }
  return usage;
}

/** The group's choices that are not removed. */
function aliveChoices(group: Pick<IOption, 'optionItems'>) {
  return (group.optionItems ?? []).filter((choice) => !choice.deletedAt);
}

export function choicesCount(row: IOptionLinkRow): number {
  if (row.draft) return row.draft.optionItems.length;
  return row.option ? aliveChoices(row.option).length : 0;
}

/**
 * «Usar una copia solo aquí»: the group as a new, independent draft with
 * the rule this product applies (legacy overrides merged in).
 */
export function draftFromGroup(
  group: IOption,
  overrides: ILinkOverrides | null = null
): ICreateOptionDto {
  const rule = effectiveRule(
    {
      type: group.type,
      multiple: group.multiple,
      mandatory: group.mandatory,
      min: group.min ?? null,
      max: group.max ?? null,
    },
    overrides
  );
  return {
    title: group.title,
    description: group.description ?? null,
    main: false,
    ...rule,
    optionItems: aliveChoices(group)
      .slice()
      .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
      .map((choice, index) => ({
        title: choice.title,
        description: choice.description ?? null,
        picture: choice.picture ?? null,
        cost: Number(choice.cost),
        discountedCost:
          choice.discountedCost != null ? Number(choice.discountedCost) : null,
        active: choice.active,
        index,
      })),
  };
}

/** A group a product of `menuId` can link: of that menu, or of none (the API rule). */
function inMenu(group: IOption, menuId: number | null): boolean {
  const groupMenu = group.menuId ?? null;
  return groupMenu === menuId || groupMenu === null;
}

/** The groups a product can link: same menu or global, not sizes, not linked yet. */
export function linkableGroups(
  groups: readonly IOption[],
  menuId: number | null,
  linkedIds: readonly number[]
): IOption[] {
  const linked = new Set(linkedIds);
  return groups.filter(
    (group) =>
      !group.main &&
      group.id !== undefined &&
      !linked.has(group.id) &&
      inMenu(group, menuId)
  );
}

/** What «+ Agregar grupo de opciones» adds (plan §5.13). */
export type TGroupPickerResult =
  | { kind: 'template'; template: TGroupTemplate }
  | { kind: 'use'; option: IOption }
  | { kind: 'copy'; option: IOption }
  | { kind: 'fromProduct'; links: Array<{ option: IOption; active: boolean }> };

/**
 * «Copiar de otro producto»: that product's groups in order, shared, with
 * their «Oculto en este producto» state (not their per-link overrides);
 * groups already linked here, and groups of another menu (the API refuses
 * them), are skipped.
 */
export function linksToCopy(
  source: IItem,
  menuId: number | null,
  linkedIds: readonly number[]
): Array<{ option: IOption; active: boolean }> {
  const linked = new Set(linkedIds);
  return [...(source.itemOptions ?? [])]
    .filter(
      (link) =>
        !!link.option &&
        !link.option.main &&
        link.option.id !== undefined &&
        !linked.has(link.option.id) &&
        inMenu(link.option, menuId)
    )
    .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
    .flatMap((link) =>
      link.option ? [{ option: link.option, active: link.active }] : []
    );
}
